'use client';
import clsx from 'clsx';
import React, {
  PropsWithChildren, useCallback, useEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';

import styles from './modal.module.scss';


type SizePreset = 'small' | 'medium' | 'large';

type Props = {
  className?: string;
  // Enable manual resizing via a bottom-right handle
  resizable?: boolean;
  // A stable identifier for this modal type to persist size in localStorage
  modalType?: string;
  // Initial/default size (precedence lower than stored size):
  // preset string: 'small' | 'medium' | 'large' (responsive to viewport)
  initialSize?: SizePreset;
  // Minimum size constraints (pixels)
  minSize?: { width?: number; height?: number };
  // Maximum size constraints (pixels). If omitted, will clamp to viewport margins.
  maxSize?: { width?: number; height?: number };
};

type Size = { width?: number | string; height?: number | string };

const STORAGE_PREFIX = 'modal:size:';

function coercePxOrCss( v?: number | string ): number | string | undefined {
  return v;
}

function parseStoredSize( raw: string | null ): Size | undefined {
  try {
    if ( !raw ) return undefined;
    const parsed = JSON.parse( raw );
    if ( parsed && ( 'width' in parsed || 'height' in parsed ) ) return parsed as Size;
  } catch { /* noop */ }
  return undefined;
}

export default function Modal( props: PropsWithChildren & Props ) {
  const { children, className, resizable = false, modalType, initialSize, minSize, maxSize } = props;

  const presetToSize = useCallback( ( preset: SizePreset ): Size => {
    switch ( preset ) {
      case 'large':
        return { width: '80vw', height: '80vh' };
      case 'medium':
        return { width: '70vw', height: '70vh' };
      case 'small':
      default:
        return { width: '60vw', height: '60vh' };
    }
  }, [] );

  const resolveInitialSize = useCallback( (): Size | undefined => {
    // 1) from storage if modalType provided
    if ( typeof window !== 'undefined' && modalType ) {
      const stored = parseStoredSize( window.localStorage.getItem( `${ STORAGE_PREFIX }${ modalType }` ) );
      if ( stored ) return stored;
    }
    // 2) from provided initialSize
    if ( initialSize === 'small' || initialSize === 'medium' || initialSize === 'large' ) {
      return presetToSize( initialSize );
    }
    // 3) fallback undefined -> let CSS control
    return undefined;
  }, [ initialSize, modalType, presetToSize ] );

  const containerRef = useRef<HTMLDivElement | null>( null );
  const [ size, setSize ] = useState<Size | undefined>( undefined );
  const [ isResizing, setIsResizing ] = useState( false );

  // Initialize size on mount
  useEffect( () => {
    setSize( resolveInitialSize() );
  }, [ resolveInitialSize ] );

  // Persist size when finished resizing
  useEffect( () => {
    if ( !modalType ) return;
    if ( !size ) return;
    try {
      window.localStorage.setItem( `${ STORAGE_PREFIX }${ modalType }`, JSON.stringify( size ) );
    } catch { /* ignore quota errors */ }
  }, [ modalType, size ] );

  const clamp = useCallback( ( next: { width: number; height: number } ) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxW = maxSize?.width ?? Math.floor( vw * 0.95 );
    const maxH = maxSize?.height ?? Math.floor( vh * 0.9 );
    const minW = Math.max( 300, minSize?.width ?? 300 );
    const minH = Math.max( 200, minSize?.height ?? 200 );
    return {
      width: Math.min( Math.max( next.width, minW ), maxW ),
      height: Math.min( Math.max( next.height, minH ), maxH )
    };
  }, [ maxSize?.height, maxSize?.width, minSize?.height, minSize?.width ] );

  const startPosRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>( null );

  const onMouseDown = useCallback( ( e: React.MouseEvent ) => {
    if ( !containerRef.current ) return;
    const rect = containerRef.current.getBoundingClientRect();
    startPosRef.current = { startX: e.clientX, startY: e.clientY, startW: rect.width, startH: rect.height };
    setIsResizing( true );
    e.preventDefault();
  }, [] );

  useEffect( () => {
    if ( !resizable ) return;
    function onMove( e: MouseEvent ) {
      if ( !startPosRef.current ) return;
      const dx = e.clientX - startPosRef.current.startX;
      const dy = e.clientY - startPosRef.current.startY;
      const next = clamp( { width: startPosRef.current.startW + dx, height: startPosRef.current.startH + dy } );
      setSize( { width: next.width, height: next.height } );
    }
    function onUp() {
      startPosRef.current = null;
      setIsResizing( false );
    }
    window.addEventListener( 'mousemove', onMove );
    window.addEventListener( 'mouseup', onUp );
    return () => {
      window.removeEventListener( 'mousemove', onMove );
      window.removeEventListener( 'mouseup', onUp );
    };
  }, [ clamp, resizable ] );

  return (
    createPortal( (
      // The modal renders via a portal but React synthetic events still
      // bubble through the React tree, so a click inside can reach an
      // ancestor (e.g. a listing row's onRowClick). Stop propagation at the
      // overlay to isolate the modal from those ancestors.
      <div className={ clsx( styles.modal_overlay ) } onClick={ ( e ) => e.stopPropagation() }>
        <div
          ref={ containerRef }
          className={ clsx( styles.modal_container, className, resizable && styles.modal_is_resizable ) }
          style={ size ? { width: coercePxOrCss( size.width ), height: coercePxOrCss( size.height ) } : undefined }
          data-testid="modal-container"
        >
          {/*
            Use an inner scrollable body so the container itself doesn't
            show scrollbars. This ensures the bottom-right resize handle
            remains fully clickable and isn't obscured by the scrollbar.
          */}
          <div className={ styles.modal_body }>
            { children }
          </div>
          { resizable && (
            <div
              className={ clsx( styles.modal_resize_handle, isResizing && styles.modal_resizing ) }
              onMouseDown={ onMouseDown }
              role="separator"
              aria-label="Resize"
              data-testid="modal-resize-handle"
            />
          ) }
        </div>
      </div>
    ), document.body )
  );
}